import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tasks from "./pages/Tasks";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectTasksPage from "./pages/ProjectTasksPage";
import ProjectTaskDetailPage from "./pages/ProjectTaskDetail";
import Calendar from "./pages/Calendar";
import Profile from "./pages/Profile";
import TaskDetail from "./pages/TaskDetail";
import MeetingDetail from "./pages/MeetingDetail";
import LoginLayout from "./layouts/LoginLayout";
import ProtectedLayout from "./layouts/ProtectedLayout";
import NetworkStatusNotification from "./components/Common/NetworkStatusNotification";
import InstallPromptDialog from "./components/InstallPromptDialog";
import ProtectedProjectRoute from "./components/Common/ProtectedProjectRoute";
import { usePWAInstall } from "./hooks/usePWAInstall";

const TitleUpdater: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const map: Record<string, string> = {
      "/": "Панель",
      "/tasks": "Задачи",
      "/projects": "Проекты",
      "/calendar": "Календарь",
      "/profile": "Профиль",
    };
    let title = "";
    if (location.pathname.startsWith("/task/")) {
      title = "Задача";
    } else if (location.pathname.startsWith("/meeting/")) {
      title = "Встреча";
    } else if (location.pathname.startsWith("/project/") && location.pathname.includes("/tasks")) {
      title = "Задачи проекта";
    } else if (location.pathname.startsWith("/project/")) {
      title = "Проект";
    } else {
      title = map[location.pathname] ?? "";
    }
    document.title = `SmarsPWA | ${title || "Страница"}`;
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const { showInstallPrompt, handleInstall, handleDismiss } = usePWAInstall();

  return (
    <Router>
      <TitleUpdater />
      <NetworkStatusNotification />
      <InstallPromptDialog
        open={showInstallPrompt}
        onInstall={handleInstall}
        onDismiss={handleDismiss}
      />

      <Routes>
        <Route
          path="/login"
          element={
            <LoginLayout>
              <Login />
            </LoginLayout>
          }
        />

        <Route path="/" element={<ProtectedLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="tasks" element={<Tasks />} />
          <Route
            path="projects"
            element={
              <ProtectedProjectRoute>
                <Projects />
              </ProtectedProjectRoute>
            }
          />
          <Route
            path="project/:id"
            element={
              <ProtectedProjectRoute>
                <ProjectDetail />
              </ProtectedProjectRoute>
            }
          />
          <Route
            path="project/:projectId/tasks"
            element={
              <ProtectedProjectRoute>
                <ProjectTasksPage />
              </ProtectedProjectRoute>
            }
          />
          <Route
            path="project/:projectId/task/:id"
            element={
              <ProtectedProjectRoute>
                <ProjectTaskDetailPage />
              </ProtectedProjectRoute>
            }
          />
          <Route path="calendar" element={<Calendar />} />
          <Route path="profile" element={<Profile />} />
          <Route path="task/:id" element={<TaskDetail />} />
          <Route path="meeting/:id" element={<MeetingDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;
